import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPause, faPlay } from "@fortawesome/free-solid-svg-icons";
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.min.js";
import { GET_NEWS_BULLETIN, GET_NEWS_BULLETIN_LIST } from "../../utils/constants";
import { makeAuthenticatedGet } from "../../utils/fetchUtils";
import Timeline from "../../components/NewsPage/Timeline";
import "./InfoAccess.css";

const LANGUAGE_OPTIONS = [
  { label: "English", value: "english" },
  { label: "Hindi", value: "hindi" },
];

const NewsBulletinRow = ({
  item,
  rowIndex,
  language,
  isPlaying,
  isExpanded,
  onPlayPause,
  announceMessage,
}) => {
  const [audioBlobUrl, setAudioBlobUrl] = useState(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);

  useEffect(() => {
    if (!isPlaying || audioBlobUrl || isLoadingAudio) {
      return;
    }

    const fetchAudio = async () => {
      setIsLoadingAudio(true);
      announceMessage("Loading audio...");
      try {
        const response = await axios.get(`${GET_NEWS_BULLETIN}/${language}/${item}`, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([response.data], { type: "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        setAudioBlobUrl(url);
        announceMessage("Audio loaded, ready to play.");
      } catch (error) {
        console.error("Error fetching audio:", error);
        setAudioBlobUrl(null);
        announceMessage("Failed to load audio.");
      } finally {
        setIsLoadingAudio(false);
      }
    };

    fetchAudio();
  }, [announceMessage, audioBlobUrl, isLoadingAudio, isPlaying, item, language]);

  useEffect(() => {
    const currentAudioBlobUrl = audioBlobUrl;
    return () => {
      if (currentAudioBlobUrl) {
        URL.revokeObjectURL(currentAudioBlobUrl);
      }
    };
  }, [audioBlobUrl]);

  useEffect(() => {
    setAudioBlobUrl(null);
    setIsLoadingAudio(false);
  }, [item, language]);

  const timelinePanelId = `news-timeline-${language}-${rowIndex}`;
  const rowTitleId = `news-title-${language}-${rowIndex}`;

  return (
    <>
      <tr>
        <td className="news-action-col">
          <Button
            className="mx-2"
            variant="primary"
            onClick={onPlayPause}
            disabled={isLoadingAudio}
            aria-expanded={isExpanded}
            aria-controls={timelinePanelId}
            aria-label={isPlaying ? `Pause news for ${item}` : `Play news for ${item}`}
          >
            {isLoadingAudio ? (
              <Spinner animation="border" size="sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              <>
                <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className="me-2" />
                {isPlaying ? "Pause" : "Play"}
              </>
            )}
          </Button>
        </td>
        <td className="chapter-name vertical-center" id={rowTitleId}>{item}</td>
      </tr>

      {isExpanded && (
        <tr className="timeline-row">
          <td colSpan={2}>
            {audioBlobUrl ? (
              <div id={timelinePanelId} role="region" aria-labelledby={rowTitleId}>
                <Timeline
                  audioUrl={audioBlobUrl}
                  isPlaying={isPlaying}
                  onPlayPause={onPlayPause}
                />
              </div>
            ) : (
              <div
                id={timelinePanelId}
                className="timeline-loading"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                Loading audio...
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

function AccessNews() {
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);
  const liveRegionRef = useRef(null);

  const storedLanguage = (localStorage.getItem("newsLanguage") || "english").toLowerCase();
  const [language, setLanguage] = useState(storedLanguage === "hindi" ? "hindi" : "english");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newsList, setNewsList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [playingIndex, setPlayingIndex] = useState(null);
  const [lastPlayedIndex, setLastPlayedIndex] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(storedLanguage === "hindi" ? "hindi" : "english");

  const announceMessage = (nextMessage) => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = "";
      setTimeout(() => {
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = nextMessage;
        }
      }, 100);
    }
  };

  const handlePlayPause = (index) => {
    setPlayingIndex((prevIndex) => {
      if (prevIndex === index) {
        setLastPlayedIndex(index);
        return null;
      }

      setLastPlayedIndex(index);
      return index;
    });
  };

  const fetchNewsList = async () => {
    try {
      setLoadingList(true);
      setError("");
      setMessage("");

      const url = `${GET_NEWS_BULLETIN_LIST}/${language}?page=${page}`;
      const response = await makeAuthenticatedGet(url);

      setNewsList(Array.isArray(response?.list) ? response.list : []);
      setTotalPages(Number(response?.totalPages) > 0 ? Number(response.totalPages) : 1);
    } catch (fetchError) {
      console.error("Error fetching bulletin list:", fetchError);
      setNewsList([]);
      setError("Failed to fetch accessible news bulletins.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchNewsList();
    document.title = "Accessible News - Shravan Portal";
  }, [language, page]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  useEffect(() => {
    setPlayingIndex(null);
    setLastPlayedIndex(null);
  }, [language, page]);

  const onPageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) {
      return;
    }
    setPage(newPage);
  };

  const onLanguageChange = (newLanguage) => {
    if (newLanguage === language) {
      return;
    }
    localStorage.setItem("newsLanguage", newLanguage);
    setLanguage(newLanguage);
    setPage(1);
  };

  return (
    <div className="container">
      <section className="header-wrap d-flex justify-content-start align-items-center">
        <button
          onClick={() => {
            setPlayingIndex(null);
            navigate(-1);
          }}
          className="btn btn-secondary"
          aria-label="Go back to previous page"
        >
          Go Back
        </button>
        <h1 className="logo my-auto ms-4">
          <span className="assistive-text">Shravan Audiobooks Library</span>
        </h1>
      </section>

      <div className="main-content mt-5" ref={mainRef} tabIndex={-1}>
        <div className='d-flex flex-row flex-wrap justify-content-end align-items-center w-100'>
          <h2 className='me-3 my-auto text-left language-font'>Select a language</h2>
          <select className='form-select w-auto' value={language} onChange={(e) => onLanguageChange(e.target.value)}>
            {
              LANGUAGE_OPTIONS && LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.value.toLowerCase()} value={lang.value}>
                  {lang.label}
                </option>
              ))
            }
          </select>
        </div>

        <div className="page book-page">
          <div className="content-wrap clearfix news-content-wrap">
            <h2 className="">Accessible News</h2>
          </div>

          <p id="audio-desc" className="visually-hidden">
            Press play to listen to a selected news bulletin.
          </p>
          <div
            ref={liveRegionRef}
            aria-live="polite"
            role="status"
            aria-atomic="true"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
              overflow: "hidden",
            }}
          ></div>

          {loadingList ? (
            <div>
              <div className="mx-3 spinner-border text-primary mt-3" role="status"></div>
              <span className="visually-hidden" aria-live="polite" aria-atomic="true">
                Loading accessible news bulletin list.
              </span>
            </div>
          ) : (
            <table className="chapter-list table table-striped mt-4">
              <caption className="visually-hidden">
                Accessible news bulletin list with play controls.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Action</th>
                  <th scope="col">Title</th>
                </tr>
              </thead>
              <tbody>
                {newsList.map((newsItem, index) => (
                  <NewsBulletinRow
                    key={`${newsItem}-${index}`}
                    item={newsItem}
                    rowIndex={index}
                    language={language}
                    isPlaying={playingIndex === index}
                    onPlayPause={() => handlePlayPause(index)}
                    isExpanded={playingIndex === index || lastPlayedIndex === index}
                    announceMessage={announceMessage}
                  />
                ))}
              </tbody>
            </table>
          )}

          {!loadingList && newsList.length === 0 && !error && (
            <p className="text-muted mt-3">No bulletin found for the selected language.</p>
          )}

          <div className="news-pagination-wrap mt-3">
            <Button
              variant="outline-secondary"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || loadingList}
            >
              Previous
            </Button>
            <span className="news-pagination-text">Page {page} of {totalPages}</span>
            <Button
              variant="outline-secondary"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages || loadingList}
            >
              Next
            </Button>
          </div>

          <div role="alert" className="mt-3">
            {message && <p className="text-success">{message}</p>}
            {error && <p style={{ color: "red" }}>Error: {error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessNews;
